// Reactive global store for Deep Research. Single engine instance per app.
// Components subscribe selectively to keep renders cheap.

import { create } from "zustand";
import { DeepResearchEngine } from "@/lib/deep-research/engine";
import { DEFAULT_GUARDRAILS, SEED_DEMO_QUERY } from "@/lib/deep-research/config";
import type {
  AuditEntry, Dossier, EngineStatus, Guardrails, PlanNode, PlanTree, ResearchRunState, SubAgentState,
} from "@/lib/deep-research/types";

interface ResearchStore {
  enabled: boolean;
  setEnabled: (v: boolean) => void;

  status: EngineStatus;
  query: string;
  plan?: PlanTree;
  agents: Record<string, SubAgentState>;
  audit: AuditEntry[];
  metrics: { tokensUsed: number; costUsd: number; confidence: number; risk: number };
  dossier?: Dossier;
  startedAt?: number;
  guardrails: Guardrails;
  setGuardrails: (g: Partial<Guardrails>) => void;

  // controls
  start: (q?: string, opts?: { agents?: number; depth?: number; branching?: number }) => Promise<void>;
  pause: () => void;
  resume: () => void;
  rollback: () => void;
  stop: () => void;
  reset: () => void;

  // selected citation in dossier viewer
  selectedCitation: number | null;
  setSelectedCitation: (n: number | null) => void;
}

const engine = new DeepResearchEngine();

export const useResearch = create<ResearchStore>((set, get) => {
  // Wire engine -> store
  engine.on((e) => {
    switch (e.type) {
      case "engine:status":
        set({ status: e.status });
        break;
      case "plan:set":
        set({ plan: e.plan });
        break;
      case "plan:nodeUpdate": {
        const cur = get().plan;
        if (!cur) return;
        const node: PlanNode | undefined = cur.nodes[e.nodeId];
        if (!node) {
          // first time we see this node
          if (e.patch && (e.patch as PlanNode).id) {
            const merged = { ...cur, nodes: { ...cur.nodes, [e.nodeId]: e.patch as PlanNode } };
            set({ plan: merged });
          }
          return;
        }
        const next = { ...node, ...e.patch };
        set({ plan: { ...cur, nodes: { ...cur.nodes, [e.nodeId]: next } } });
        break;
      }
      case "swarm:spawn":
        set((s) => ({ agents: { ...s.agents, [e.agent.id]: e.agent } }));
        break;
      case "swarm:agentUpdate":
        set((s) => {
          const cur = s.agents[e.agentId];
          if (!cur) return s;
          return { agents: { ...s.agents, [e.agentId]: { ...cur, ...e.patch } } };
        });
        break;
      case "audit":
        set((s) => ({ audit: [...s.audit.slice(-499), e.entry] }));
        break;
      case "metrics":
        set({ metrics: { tokensUsed: e.tokensUsed, costUsd: e.costUsd, confidence: e.confidence, risk: e.risk } });
        break;
      case "dossier":
        set({ dossier: e.dossier });
        break;
      case "error":
        set({ status: "failed" });
        break;
    }
  });

  return {
    enabled: false,
    setEnabled: (v) => set({ enabled: v }),

    status: "idle",
    query: "",
    agents: {},
    audit: [],
    metrics: { tokensUsed: 0, costUsd: 0, confidence: 0, risk: 0.18 },
    guardrails: { ...DEFAULT_GUARDRAILS },
    setGuardrails: (g) => set((s) => ({ guardrails: { ...s.guardrails, ...g } })),

    selectedCitation: null,
    setSelectedCitation: (n) => set({ selectedCitation: n }),

    async start(q, opts) {
      // Strict: never silently substitute an empty query with the demo seed.
      // The caller (NeuralChat) is responsible for validation. We only fall
      // back to the demo query when start() is invoked with no argument at all
      // (e.g. programmatic demo trigger), NOT when an empty/whitespace string
      // is passed — that would mask UI bugs.
      const trimmed = typeof q === "string" ? q.trim() : "";
      const query = trimmed.length > 0 ? trimmed : (q === undefined ? SEED_DEMO_QUERY : "");
      if (!query) {
        // Refuse to start with an empty query: surface the bug instead of hiding it.
        throw new Error("Deep Research: empty query rejected");
      }
      set({
        status: "planning",
        query,
        plan: undefined,
        agents: {},
        audit: [],
        metrics: { tokensUsed: 0, costUsd: 0, confidence: 0, risk: 0.18 },
        dossier: undefined,
        startedAt: performance.now(),
        selectedCitation: null,
      });
      try {
        await engine.run({
          query,
          guardrails: get().guardrails,
          agents: opts?.agents,
          depth: opts?.depth,
          branching: opts?.branching,
        });
      } catch {
        // engine emits its own error events
      }
    },
    pause() { engine.pause(); },
    resume() { engine.resume(); },
    rollback() { engine.rollback(); set({ agents: {}, dossier: undefined }); },
    stop() { engine.stop(); },
    reset() {
      set({
        status: "idle",
        query: "",
        plan: undefined,
        agents: {},
        audit: [],
        metrics: { tokensUsed: 0, costUsd: 0, confidence: 0, risk: 0.18 },
        dossier: undefined,
        startedAt: undefined,
        selectedCitation: null,
      });
    },
  };
});

// Re-export the type for convenience
export type { ResearchRunState };
